import { Box, Flex, Text } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { fonts, mineral } from '../../../theme'
import { TONES, type Tone } from '../../../components/analytics/reportTheme'
import AnimatedNumber from '../../../components/brand/AnimatedNumber'

/**
 * Puntaje como medidor: la cifra en una píldora del tono del nivel y, debajo, una
 * barrita 0–100 que se llena al aparecer. Para la tabla.
 */
export function ScoreMeter({ score, tone }: { score: number; tone: Tone }) {
  const t = TONES[tone]
  return (
    <Flex direction="column" gap={1} w="76px">
      <Flex align="baseline" gap={0.5}>
        <Box as="span" px={2} py="1px" borderRadius="8px" bg={t.soft} color={t.deep}
          fontFamily={fonts.display} fontWeight={800} fontSize="15px" lineHeight="1.35" minW="38px" textAlign="center">
          {score.toLocaleString('es-PE')}
        </Box>
        <Text as="span" fontSize="10.5px" color={mineral.ink4}>/100</Text>
      </Flex>
      <Box h="4px" borderRadius="full" bg={mineral.sunk} overflow="hidden">
        <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min(score, 100)}%` }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          style={{ height: '100%', background: t.color, borderRadius: 999 }} />
      </Box>
    </Flex>
  )
}

/**
 * Medidor circular para el detalle. El arco se dibuja hasta el puntaje cuando se
 * abre el proveedor (o cambia de proveedor).
 */
export function ScoreRing({ score, tone, size = 112 }: { score: number; tone: Tone; size?: number }) {
  const t = TONES[tone]
  const stroke = 10
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  const pct = Math.min(Math.max(score, 0), 100) / 100
  return (
    <Box position="relative" w={`${size}px`} h={`${size}px`} flexShrink={0}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={mineral.sunk} strokeWidth={stroke} />
        <motion.circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={t.color} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={c}
          initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: c * (1 - pct) }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }} />
      </svg>
      <Flex position="absolute" inset={0} direction="column" align="center" justify="center">
        <Text fontFamily={fonts.display} fontWeight={800} fontSize="30px" lineHeight="1" color={t.deep} letterSpacing="-0.03em">
          <AnimatedNumber value={score} duration={0.9} />
        </Text>
        <Text fontSize="11px" color={mineral.ink3} mt={0.5}>de 100</Text>
      </Flex>
    </Box>
  )
}
